<script setup>
import { ref, computed, onMounted, defineProps } from 'vue';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';
import Toast from '@/components/toast.vue';
import Stepper from '@/components/Stepper.vue';
import { useRouter, useRoute } from 'vue-router';
import { useStorage } from '@/composables/use-storage';
import { useToast } from '@/composables/use-toast';
import axios from '@/plugins/axios';
import { prepareEvalBuilderFiles } from '@/pages/dataset/services/eval/evalBuilderFileService';
import { uploadFile } from '@/services/fileService';
import { validateReferenceFileContent } from '@/pages/dataset/services/eval/evalDatasetValidationService';
import Modal from '@/components/modals/Modal.vue'
import EvalBuilder from '@/pages/dataset/components/eval/EvalBuilder.vue'
import LoaderWithText from '@/components/LoaderWithText.vue';

import {
    createDataset, 
    uploadDatasetFile,
    fetchSellers,
    fetchDatasetById,
    updateDataset,
    fetchSellerById,
    deleteDataset,
    updateDatasetOwner,
    uploadEvalFile,
    deleteDatasetFile
    } from '@/services/api';

const props = defineProps({
    id: {
        type: [String, Number],
        default: null
    }
});

const router = useRouter();
const route = useRoute();


const { TextInput, InputError, ButtonLoading, SelectInput, Alert, ComboboxInput, MarkdownRenderer } = useLayoutComponents();
const { userInfo } = useStorage();
const { User } = useIcons();
const { addToast } = useToast();

const steps = computed(() => [
    'Dataset',
    'Listing Type',
    'Listing Details',
]);

const currentStep = computed(() => {
    return 1;
});

const datasetName = ref('');
const initCommit = ref('');
const selectedFile = ref(null);
const uploadProgress = ref(0);
const isUploading = ref(false);
const fileSchema = ref(null);
const errors = ref({
    datasetName: '',
    initCommit: '',
    file: '',
    seller: '',
    connectionType: ''
});

const isSubmitting = ref(false);
const isDragging = ref(false);
const hasUploadError = ref(false);

const showAlert = ref(false);
const alertTitle = ref('');
const alertMessage = ref('');
const alertType = ref('info');
const datasetId = ref(props.id || route.params.id);
const showDeleteAlert = ref(false);
const alertShowPasswordInput = ref(false);
const showUpdateOwnerAlert = ref(false);

const isAdmin = computed(() => userInfo.value?.is_admin === true);

const selectedOwner = ref('');
const originalOwner = ref('');
const originalOwnerName = ref('');
const ownersList = ref([]);

const isEditing = computed(() => {
    return route.name === 'dataset-edit';
});

const isEditingEvalDataset = ref(false)

const datasetIdParam = computed(() => {
    return props.id || route.params.id;
});

const showFileExistsAlert = ref(false);
const existingFileName = ref('');
const fileExistsError = ref('');
const showRemoveFileAlert = ref(false);

const isLoadingDataset = ref(false);
const isDeleting = ref(false);

// Tooltip refs and functions
const tooltipVisible = ref(false);
const tooltipPosition = ref({ x: 0, y: 0 });
const tooltipTrigger = ref(null);

const allowedExtensions = ref(['.zip', '.tar.gz', '.parquet']);

const showTooltip = (event) => {
    if (tooltipTrigger.value) {
        const rect = tooltipTrigger.value.getBoundingClientRect();
        tooltipPosition.value = {
            x: rect.left + rect.width / 2,
            y: rect.top
        };
        tooltipVisible.value = true;
    }
};

const hideTooltip = () => {
    tooltipVisible.value = false;
};

onMounted(async () => {
    if (isAdmin.value) {
        fetchSellers().then((sellers) => {
            ownersList.value = sellers.map((seller) => {
                if (seller) {
                    return {
                        value: seller.user_id,
                        profilePicture: seller.user?.profile_pic,
                        label: seller.user?.username
                    }
                }
            }).filter(seller => seller !== null && seller !== undefined && seller !== '');
        });
    } else if (userInfo.value.id) {
        await fetchSellerById(userInfo.value.id).then((seller) => {
            selectedOwner.value = seller.user.id;
            originalOwner.value = seller.user.id;
            originalOwnerName.value = seller.user.username;
        });
    }
    
    if (isEditing.value) {
        if (datasetIdParam.value) {
            await loadDataset(datasetIdParam.value);
        }
    }
});

// Helpers to simplify dataset loading flow and reduce cognitive complexity
const applyDatasetBasics = (dataset) => {
    datasetName.value = dataset.name;
    initCommit.value = dataset.description;
    datasetId.value = dataset.id;
};

const applyDatasetType = async (dataset) => {
    if (dataset.eval_id) {
        datasetType.value = 'eval';
        isEditingEvalDataset.value = true;
        evalId.value = dataset.eval_id;
        return;
    }

    if (dataset.external_source_id || !dataset.file_name) {
        await defineExternalDatasetInfos(dataset);
    }
};

const applyOwnerIfAdmin = (dataset) => {
    if (dataset.seller?.user?.id && isAdmin.value) {
        selectedOwner.value = dataset.seller.user.id;
        originalOwner.value = dataset.seller.user.id;
        originalOwnerName.value = dataset.seller.user.username;
    }
};

const applyFileFromDataset = (dataset) => {
    if (dataset.file_name && dataset.file_extension) {
        selectedFile.value = {
            name: dataset.file_name,
            size: dataset.file_size || 0
        };
        if (dataset.schema) {
            fileSchema.value = dataset.schema;
        }
    }
};

const loadDataset = async (id) => {
    isLoadingDataset.value = true;
    try {
        const dataset = await fetchDatasetById(id);
        if (!dataset) return;

        applyDatasetBasics(dataset);
        await applyDatasetType(dataset);
        applyOwnerIfAdmin(dataset);
        applyFileFromDataset(dataset);
    } catch (error) {
        console.error('Error loading dataset:', error);
        addToast('Error loading dataset information', 'error');
    } finally {
        isLoadingDataset.value = false;
    }
};

const defineExternalDatasetInfos = async (dataset) => {
    datasetType.value = 'external';
    await getConnectionTypes()
    connectionType.value = dataset.external_source_id;
    estimatedSize.value = dataset.file_size > 0 ? convertSizeTo(dataset.file_size, 'GB') : null;

}

const username = computed(() => {
    if (isAdmin.value && selectedOwner.value) {
        const owner = ownersList.value.find(owner => owner.value === selectedOwner.value);
        return owner ? owner.label : '';
    }
    return userInfo.value?.username || 'User';
});

const profilePic = computed(() => {
    return localStorage.getItem('profilePicture');
});

const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
        handleFileUpload(file);
    }
};

const handleClearFile = (event) => {
    event.stopPropagation();
    selectedFile.value = null;
    // Reset the file input
    const fileInput = document.getElementById('coreDatasetFile');
    if (fileInput) {
        fileInput.value = '';
    }
};

const handleFileDelete = async () => {
    if (!selectedFile.value) return;
    isDeleting.value = true;
    const fileInput = document.getElementById('fileInput');
    if (fileInput) {
        fileInput.value = '';
    }
    const response = await deleteDatasetFile(selectedFile.value?.name);
    if (response.status === 200 || response.status === 201) {
        addToast('File deleted successfully', 'success');
    }
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const removeSelectedFile = async () => {
    showRemoveFileAlert.value = true;
    alertTitle.value = 'Confirm File Removal';
    alertMessage.value = 'Are you sure you want to remove this file? You will need to upload a new file to continue.';
    alertType.value = 'warning';
    alertShowPasswordInput.value = false;
};

const confirmRemoveFile = () => {
    handleFileDelete();
    selectedFile.value = null;
    uploadProgress.value = 0;
    hasUploadError.value = false;
    errors.value.file = '';
    showRemoveFileAlert.value = false;
    
    const fileInput = document.getElementById('fileInput');
    if (fileInput) {
        fileInput.value = '';
    }
};

const cancelRemoveFile = () => {
    showRemoveFileAlert.value = false;
};

const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    isDragging.value = false;
};

const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isLoadingDataset.value || isUploading.value) return;
    isDragging.value = true;
};

const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    isDragging.value = false;
    
    if (isLoadingDataset.value || isUploading.value) return;
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
        const file = files[0];
        handleFileUpload(file);
    }
};

const handleFileUpload = async (file) => {
    if (isLoadingDataset.value || isUploading.value) return;
    if (!file) return;

    const isValidExtension = allowedExtensions.value.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValidExtension) {
        errors.value.file = `Please select a valid file (${allowedExtensions.value.join(', ')})`;
        hasUploadError.value = true;
        return;
    }
    
    errors.value.file = '';
    selectedFile.value = file;
    hasUploadError.value = false;
    uploadProgress.value = 0;
};

const handleFileExistsConfirm = async () => {
    showFileExistsAlert.value = false;
    uploadProgress.value = 100;
    isUploading.value = false;
    hasUploadError.value = false;
    isSubmitting.value = true;

    const datasetInfo = prepareDatasetInfo();

    try {
        if (isEditing.value) {
            const response = await updateDataset(datasetInfo, datasetId.value);
            if (response.status === 200 || response.status === 201) {
                router.push({ name: 'creator-profile', query: { tab: 'datasets' } });
            } else {
                throw new Error(response.message);
            }
        } else {
            const response = await createDataset(datasetInfo);
            if (response.status === 200 || response.status === 201) {
                datasetId.value = response.data.id;
                showAlert.value = true;
                alertTitle.value = 'Dataset created successfully!';
                alertMessage.value = 'Would you like to create a listing for this dataset now?';
                alertType.value = 'success';
            } else {
                throw new Error(response.message);
            }
        }
    } catch (error) {
        console.error('Error creating/updating dataset:', error);
        addToast(error, 'error');
    } finally {
        isSubmitting.value = false;
    }
};

const handleCancelFileExists = () => {
    showFileExistsAlert.value = false;
    fileExistsError.value = '';
};

const validateDatasetForm = () => {
    errors.value = {
        datasetName: '',
        initCommit: '',
        file: '',
        seller: '',
        connectionType: ''
    };

    const currentErrors = errors.value;
    const missingFields = [];
    const currentType = datasetType.value;
    
    const validationRules = [
        {
            condition: () => isAdmin.value && !selectedOwner.value,
            types: ['internal', 'external', 'eval', 'eval-builder'],
            key: 'seller',
            message: 'Please select an owner',
            label: 'Owner'
        },
        {
            condition: () => !datasetName.value,
            types: ['internal', 'external', 'eval', 'eval-builder'],
            key: 'datasetName',
            message: {
                eval: 'Core dataset name is required',
                'eval-builder': 'Dataset name is required',
                internal: 'Dataset name is required',
                external: 'Dataset name is required'
            },
            label: {
                eval: 'Core Dataset Name',
                'eval-builder': 'Dataset Name',
                internal: 'Dataset Name',
                external: 'Dataset Name'
            }
        },
        {
            condition: () => !initCommit.value,
            types: ['internal'],
            key: 'initCommit',
            message: 'Initial commit message is required',
            label: 'Init Commit'
        },
        {
            condition: () => !selectedFile.value,
            types: ['internal'],
            key: 'file',
            message: 'Please select a file',
            label: 'File'
        },
        {
            condition: () => !connectionType.value,
            types: ['external'],
            key: 'connectionType',
            message: 'Connection type is required for external datasets',
            label: 'Connection Type'
        },
        {
            condition: () => !isEditing.value && !taskFileSelected.value,
            types: ['eval'],
            key: 'file',
            message: '',
            label: 'Task file'
        },
        {
            condition: () => !isEditing.value && !answerFileSelected.value,
            types: ['eval'],
            key: 'file',
            message: '',
            label: 'Answer file'
        },
        {
            condition: () => !isEditing.value && !referenceFileSelected.value && referenceFileIsRequired.value,
            types: ['eval'],
            key: 'file',
            message: '',
            label: 'Reference file'
        }
    ];

    const flagMissing = (condition, key, message, label) => {
        if (condition) {
            currentErrors[key] = message;
            missingFields.push(label);
            return 1;
        }
        return 0;
    };

    let errorsCount = 0;

    
    for (const rule of validationRules) {
        if (rule.types.includes(currentType)) {
            const message = typeof rule.message === 'object' ? rule.message[currentType] : rule.message;
            const label = typeof rule.label === 'object' ? rule.label[currentType] : rule.label;
            errorsCount += flagMissing(rule.condition(), rule.key, message, label);
        }
    }

    const isValid = errorsCount === 0;
    if (!isValid) {
        addToast(`Please fill in the following required fields: ${missingFields.join(', ')}`, 'error');
    }
    return isValid;
};

const validateOwnerUpdateForm = () => {
    errors.value = {
        datasetName: '',
        initCommit: '',
        file: '',
        seller: ''
    };

    let isValid = true;
    let missingFields = [];

    if (isAdmin.value && !selectedOwner.value) {
        errors.value.seller = 'Please select an owner';
        missingFields.push('Owner');
        isValid = false;
    }

    if (!datasetName.value) {
        errors.value.datasetName = 'Dataset name is required';
        missingFields.push('Dataset Name');
        isValid = false;
    }

    if(datasetType.value === 'external') {
        initCommit.value = 'External dataset';
    }

    if (!initCommit.value && datasetType.value === 'internal') {
        errors.value.initCommit = 'Initial commit message is required';
        missingFields.push('Init Commit');
        isValid = false;
    }

    if (!isValid) {
        addToast(`Please fill in the following required fields: ${missingFields.join(', ')}`, 'error');
    }

    return isValid;
};

const processFileInfo = () => {
    if (!selectedFile.value) return { fullFileName: '', fileExtension: '' };
    
    const fullFileName = selectedFile.value.name;
    const fileExtension = fullFileName.split('.').pop();
    
    return { fullFileName, fileExtension };
};

const prepareDatasetInfo = () => {
    const { fullFileName, fileExtension } = processFileInfo();

    const datasetInfo = {
        dataset: {
            name: datasetName.value,
            description: initCommit.value,
            owner_user_id: selectedOwner.value ? selectedOwner.value : userInfo.value?.id
        },
        comment: isEditing.value ? initCommit.value : 'Initial dataset creation'
    };

    if (selectedFile.value || !isEditing.value) {
        datasetInfo.dataset.file_name = fullFileName;
        datasetInfo.dataset.file_size = selectedFile.value?.size || 0;
        datasetInfo.dataset.file_extension = fileExtension;
        datasetInfo.dataset.schema = fileSchema.value;
    }

    if (datasetType.value === 'external') {
        datasetInfo.dataset.file_name = null;
        datasetInfo.dataset.file_extension = null;
        datasetInfo.dataset.schema = null;
        datasetInfo.dataset.external_source_id = connectionType.value;
        datasetInfo.dataset.file_size = convertSizeTo(estimatedSize.value, 'B');
    }

    if (datasetType.value === 'eval' || datasetType.value === 'eval-builder') {
        datasetInfo.dataset.eval_id = evalId.value;
        datasetInfo.dataset.reference_file_path = datasetType.value === 'eval' ? referenceFileUploadedPath.value : null;
        datasetInfo.dataset.reference_file_name = datasetType.value === 'eval' ? referenceFileSelected.value?.name : null;
    }

    return datasetInfo;
};

const startFileUpload = async () => {
    isUploading.value = true;
    uploadProgress.value = 0;
    try {
        const response = await uploadDatasetFile(
            selectedFile.value,
            selectedOwner.value,
            (progress) => {
                uploadProgress.value = progress;
            }
        );

        if (response && response.error === 'FILE_EXISTS') {
            showFileExistsAlert.value = true;
            existingFileName.value = selectedFile.value.name;
            isUploading.value = false;
            isSubmitting.value = false;
            evalFilesIsUploading.value = false;
            return;
        }

        return response;
    } catch (error) {
        errors.value.file = `Error uploading file: ${error.message || 'Unknown error'}`;
        hasUploadError.value = true;
        isUploading.value = false;
        isSubmitting.value = false;
        return;
    }   
}

const handleDatasetUpdate = async (datasetInfo, datasetId = '') => {
    const response = await updateDataset(datasetInfo, datasetId);
    if (response.status === 200 || response.status === 201) {
        router.push({ name: 'creator-profile', query: { tab: 'datasets' } });
        return;
    }
    throw new Error(response.message);
};

const handleDatasetUpdateOwner = async (datasetId, datasetInfo) => {
    const response = await updateDatasetOwner(datasetId, datasetInfo);
    if (response.status === 200 || response.status === 201) {
        return;
    }
    throw new Error(response.message);
};

const handleDatasetCreate = async (datasetInfo) => {
    const response = await createDataset(datasetInfo);
    if (response.status === 200 || response.status === 201) {
        datasetId.value = response.data.id;
        showAlert.value = true;
        alertTitle.value = 'Dataset created successfully!';
        alertMessage.value = 'Would you like to create a listing for this dataset now?';
        alertType.value = 'success';
        return;
    }
    throw new Error(response.message);
};

const taskFileSelected = ref(null);
const referenceFileSelected = ref(null);
const referenceFileUploadedPath = ref(null);
const answerFileSelected = ref(null);

const evalFilesUploadProgress = ref(0);
const evalFilesIsUploading = ref(false);
const referenceFileIsRequired = ref(false);

const evalId = ref(null);
const taskFileContent = ref(null);
const evalBuilderRef = ref(null);

const removeTaskFileSelected = () => {
    taskFileSelected.value = null;
    taskFileContent.value = null;
    referenceFileIsRequired.value = false;
    const taskFileInput = document.getElementById('taskFileInput');
    if (taskFileInput) {
        taskFileInput.value = '';
    }
}

const removeReferenceFile = () => {
    referenceFileSelected.value = null;
    referenceFileUploadedPath.value = null;
    referenceFileIsRequired.value = false;
    const referenceFileInput = document.getElementById('referenceFileInput');
    if (referenceFileInput) {
        referenceFileInput.value = '';
    }
}

const removeAnswerFileSelected = () => {
    answerFileSelected.value = null;
    const answerFileInput = document.getElementById('answerFileInput');
    if (answerFileInput) {
        answerFileInput.value = '';
    }
}
const handleEvalFilesUpload = async () => {
    isUploading.value = true;
    evalFilesIsUploading.value = true;
    try {
        const files = [taskFileSelected.value, answerFileSelected.value]
        const uploadResponse = await uploadEvalFile(
            files, 
            selectedOwner.value, (progress) => {evalFilesUploadProgress.value = progress;
        });
        evalId.value = uploadResponse;
        return uploadResponse;
    } catch (error) {
        console.error('Error uploading eval files:', error);
        addToast(error, 'error');
        isSubmitting.value = false;
    } 
    evalFilesIsUploading.value = false;
    isUploading.value = false;
}

const validEvalExtension = (name) => {
    return name.endsWith('.json') || name.endsWith('.txt') || name.endsWith('.gz');
}

const handleTaskFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
        if (!validEvalExtension(file.name)) {
            addToast('Invalid file. Use .json or .txt for Task.', 'error');
            event.target.value = '';
            return;
        }

        if (file.name.endsWith('.json')) {
            const payload = await validateTaskFileContent(file);
            if (!payload) {
                event.target.value = '';
                return;
            }
            taskFileContent.value = payload.prompts;
        }
        
        try {
            const renamed = new File([file], 'task.json', { type: 'application/json', lastModified: file.lastModified });
            taskFileSelected.value = renamed;
        } catch (e) {
            console.warn('Error renaming task file, using original file:', e);
            taskFileSelected.value = file;
        }
    } catch (error) {
        console.error('Error processing file:', error);
        addToast('Error reading file. Please try again.', 'error');
        event.target.value = '';
    }
};

const handleReferenceFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();
    const isValidExtension = fileName.endsWith('.zip') || fileName.endsWith('.tar.gz');
    if (!isValidExtension) {
        addToast('Invalid file. Use .zip or .tar.gz for Reference.', 'error');
        event.target.value = '';
        return;
    }
    try {
        const extensionMap = { '.tar.gz': 'tar.gz', '.zip': 'zip' };
        const matchedExtension = Object.keys(extensionMap).find(ext => fileName.endsWith(ext));
        const fileExtension = matchedExtension ? extensionMap[matchedExtension] : '';
        const renamed = new File([file], `refs.${fileExtension}`, { type: file.type || 'application/zip', lastModified: file.lastModified });

        const validate = await validateCompactRefFile(file);
        if (validate.error) {
            event.target.value = '';
            return;
        }
        referenceFileUploadedPath.value = validate.filePath;
        referenceFileSelected.value = renamed;
    } catch (e) {
        console.warn('Error renaming reference file, using original file:', e);
        referenceFileSelected.value = file;
        referenceFileUploadedPath.value = null;
    }
};

const isValidatingReferenceFile = ref(false)
const validatingReferenceFileStep = ref(-1)
const validateReferenceFileStepConfig = ref([
    {title: 'Initializing file validation', subtitle: 'Let\'s begin validating the content of your file'},
    {title: 'Updating file', subtitle: 'This may take a while depending on your connection'},
    {title: 'Validating file format', subtitle: 'Checking the file structure and ensuring all formats are compatible'}
])

const handleReferenceFileValidateStep = (step) =>{
    validatingReferenceFileStep.value = step
    isValidatingReferenceFile.value = step >= 0
}

const validateCompactRefFile = async (file) => {
    handleReferenceFileValidateStep(0)
    await new Promise(resolve => setTimeout(resolve, 2000));
    let result = { error: null, filePath: null };
    try {
        handleReferenceFileValidateStep(1)
        const response = await uploadFile(file, selectedOwner.value, (progress) => {evalFilesUploadProgress.value = progress;});
        handleReferenceFileValidateStep(2)
        const validateReferenceFileResponse = await validateReferenceFileContent(response.filePath);
        if (!validateReferenceFileResponse.data.valid){
            addToast(validateReferenceFileResponse.data.error, 'error');
            result.error = true;
            return result;
        }
        addToast('Reference file validated successfully', 'success');
        result.filePath = response.filePath;
        return result;
    } catch (e) {
        console.warn('Error validating reference file:', e);
        addToast(e.message, 'error');
        result.error = true;
        return result;
    } finally {
        handleReferenceFileValidateStep(-1)
    }
}

const handleAnswerFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!validEvalExtension(file.name)) {
        addToast('Invalid file. Use .json or .txt for Answer.', 'error');
        event.target.value = '';
        return;
    }
    if (file.name.endsWith('.json')) {
        const isValid = await validateAnswerFileContent(file);
        if (!isValid) {
            event.target.value = '';
            return;
        }
        // Validate task_ids compatibility
        const isCompatible = await validateTaskIdsCompatibility(file);
        if (!isCompatible) {
            event.target.value = '';
            return;
        }
    }
    try {
        const renamed = new File([file], 'answers.json', { type: 'application/json', lastModified: file.lastModified });
        answerFileSelected.value = renamed;
    } catch (e) {
        console.warn('Error renaming answer file, using original file:', e);
        answerFileSelected.value = file;
    }
};

// Drag and drop handlers for eval files
const handleEvalDragLeave = (event) => handleDragLeave(event);

const handleEvalDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (evalFilesIsUploading.value || isUploading.value || isEditingEvalDataset.value) return;
    isDragging.value = true;
};

const handleTaskFileDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    isDragging.value = false;
    
    if (evalFilesIsUploading.value || isUploading.value || isEditingEvalDataset.value) return;
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
        const file = files[0];
        if (!validEvalExtension(file.name)) {
            addToast('Invalid file. Use .json or .txt for Task.', 'error');
            return;
        }
        if (file.name.endsWith('.json')) {
            const payload = await validateTaskFileContent(file);
            if (!payload) {
                return;
            }
            taskFileContent.value = payload.prompts;
        }
        try {
            const renamed = new File([file], 'task.json', { type: 'application/json', lastModified: file.lastModified });
            taskFileSelected.value = renamed;
        } catch (e) {
            console.warn('Error renaming task file, using original file:', e);
            taskFileSelected.value = file;
        }
    }
};

const handleReferenceFileDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    isDragging.value = false;
    
    if (evalFilesIsUploading.value || isUploading.value || isEditingEvalDataset.value) return;
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
        const file = files[0];
        const fileName = file.name.toLowerCase();
        const isValidExtension = fileName.endsWith('.zip') || fileName.endsWith('.tar.gz');
        if (!isValidExtension) {
            addToast('Invalid file. Use .zip, .gz or .tar.gz for Reference.', 'error');
            return;
        }
        try {
            
            const extensionMap = { '.tar.gz': 'tar.gz', '.zip': 'zip' };
            const matchedExtension = Object.keys(extensionMap).find(ext => fileName.endsWith(ext));
            const fileExtension = matchedExtension ? extensionMap[matchedExtension] : '';
            const renamed = new File([file], `refs.${fileExtension}`, { type: file.type || 'application/zip', lastModified: file.lastModified });
            const validate = await validateCompactRefFile(file);
            if (validate.error) {
                return;
            }
            referenceFileUploadedPath.value = validate.filePath;
            referenceFileSelected.value = renamed;
        } catch (e) {
            console.warn('Error renaming reference file, using original file:', e);
            referenceFileSelected.value = file;
            referenceFileUploadedPath.value = null;
        }
    }
};

const handleAnswerFileDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    isDragging.value = false;
    
    if (evalFilesIsUploading.value || isUploading.value || isEditingEvalDataset.value) return;
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
        const file = files[0];
        if (!validEvalExtension(file.name)) {
            addToast('Invalid file. Use .json or .txt for Answer.', 'error');
            return;
        }
        if (file.name.endsWith('.json')) {
            const isValid = await validateAnswerFileContent(file);
            if (!isValid) {
                return;
            }
            // Validate task_ids compatibility
            const isCompatible = await validateTaskIdsCompatibility(file);
            if (!isCompatible) {
                return;
            }
        }
        try {
            const renamed = new File([file], 'answers.json', { type: 'application/json', lastModified: file.lastModified });
            answerFileSelected.value = renamed;
        } catch (e) {
            console.warn('Error renaming answer file, using original file:', e);
            answerFileSelected.value = file;
        }
    }
};

const handleKnowledgeFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    isDragging.value = false;
    
    if (evalFilesIsUploading.value || isUploading.value) return;
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
        const file = files[0];
        const fileName = file.name.toLowerCase();
        const isValidExtension = fileName.endsWith('.zip') || fileName.endsWith('.tar.gz');
        if (!isValidExtension) {
            addToast('Invalid file. Use .zip or .tar.gz for Core Dataset.', 'error');
            return;
        }
        handleFileUpload(file);
    }
};

const readJsonFile = async (file) => { 
    try {
        const content = await file.text();

        if (file.name.endsWith('.json')) {
            try {
                const jsonContent = JSON.parse(content);
                return jsonContent;
            } catch (error) {
                console.warn('Error parsing JSON:', error);
                throw error;
            }
        } else {
            return content;
        }
    } catch (error) {
        console.error('Error reading file:', error);
        throw new Error('Error reading file');
    }
}

const validateTaskStructure = (task) => {
    if (!task.task_id) {
        addToast('Missing task_id in task', 'error');
        return false;
    }
    if (!task.task_prompt) {
        addToast(`Missing task_prompt in task ${task.task_id} add a task prompt and try again`, 'error');
        return false;
    }
    return true;
};

const validateTaskReferenceFile = (task) => {
    if (!Object.keys(task).includes('reference_file')) {
        addToast(`Missing reference_file in task ${task.task_id}`, 'error');
        return false;
    }
    if (task.reference_file !== "") {
        referenceFileIsRequired.value = true;
    }
    return true;
};

const normalizeTasksPayload = (jsonContent) => {
    if (!jsonContent || typeof jsonContent !== 'object' || Array.isArray(jsonContent)) {
        return null;
    }
    if (!Array.isArray(jsonContent.prompts)) {
        return null;
    }
    return {
        version: jsonContent.version,
        prompts: jsonContent.prompts
    };
};

const validateTaskFileContent = async (file) => {
    try {
        const jsonContent = await readJsonFile(file);
        const payload = normalizeTasksPayload(jsonContent);
        if (!payload) {
            addToast('Invalid JSON structure: expected { version, prompts }', 'error');
            return null;
        }
        const versionNumber = Number(payload.version);
        if (!Number.isFinite(versionNumber)) {
            addToast('Invalid or missing version in task file', 'error');
            return null;
        }
        
        for (const task of payload.prompts) {
            if (!validateTaskStructure(task) || !validateTaskReferenceFile(task)) {
                return null;
            }
        }
        return payload;
    } catch (error) {
        addToast(`Error validating task file: ${error}`, 'error');
        return null;
    }
}

const validateAnswerFileContent = async (file) => {
    try {
        const jsonContent = await readJsonFile(file);
        if (!jsonContent || !Array.isArray(jsonContent)) {
            addToast('Invalid JSON structure: not an array', 'error');
            return false;
        }
        for (const answer of jsonContent) {
            if (!answer.task_id) {
                addToast('Missing task_id in answer, check the file structure and try again', 'error');
                return false;
            }
            if (answer.answer == null || answer.answer == undefined || answer.answer === "") {
                addToast(`Answer is empty in answer ${answer.task_id}, check the file structure and try again`, 'error');
                return false;
            }
        }
        return true;
    } catch (error) {
        addToast(`Error validating answer file: ${error}`, 'error');
        return false;
    }
}

const validateTaskIdsCompatibility = async (answerFile) => {
    try {
        if (!taskFileContent.value) {
            addToast('Please select a task file first before selecting an answer file', 'error');
            return false;
        }

        const answerContent = await readJsonFile(answerFile);
        if (!answerContent || !Array.isArray(answerContent)) {
            return false;
        }

        // Extract all task_ids from tasks.json
        const taskIds = new Set(taskFileContent.value.map(task => task.task_id));
        
        // Check if all answer task_ids exist in tasks
        let hasIncompatibleIds = false;
        for (const answer of answerContent) {
            if (!taskIds.has(answer.task_id)) {
                hasIncompatibleIds = true;
                break;
            }
        }

        if (hasIncompatibleIds) {
            addToast('Incompatible task_ids found. Some task_ids in answers.json do not exist in tasks.json', 'error');
            return false;
        }

        return true;
    } catch (error) {
        addToast(`Error validating task_ids compatibility: ${error}`, 'error');
        return false;
    }
}

// Handle upload for eval-builder type
const handleEvalBuilderUpload = async () => {
    if (!evalBuilderRef.value || !evalBuilderRef.value.evalBuilderTasks) {
        addToast('No eval builder tasks found', 'error');
        return null;
    }

    isUploading.value = true;
    evalFilesIsUploading.value = true;
    
    try { 
        const result = await prepareEvalBuilderFiles(evalBuilderRef.value.evalBuilderTasks);
        
        if (result.errors.length > 0) {
        
            addToast(result.errors[0], 'error');
            
            // Mark fields with errors
            if (result.fieldErrors && evalBuilderRef.value.setFieldErrors) {
                evalBuilderRef.value.setFieldErrors(result.fieldErrors);
            }
            
            isSubmitting.value = false;
            return null;
        }

        if (!result.files || result.files.length === 0) {
            addToast('No files to upload', 'error');
            isSubmitting.value = false;
            return null;
        }

        const uploadResponse = await uploadEvalFile(
            result.files,
            selectedOwner.value,
            (progress) => {
                evalFilesUploadProgress.value = progress;
            }
        );

        evalId.value = uploadResponse;
        return uploadResponse;
    } catch (error) {
        console.error('Error uploading eval builder files:', error);
        addToast(error.message || 'Error uploading eval builder files', 'error');
        isSubmitting.value = false;
        return null;
    } finally {
        evalFilesIsUploading.value = false;
        isUploading.value = false;
    }
};

const uploadCoreDatasetIfNeeded = async () => {
    if (!selectedFile.value) return true;
    const uploadResponse = await startFileUpload();
    return uploadResponse === 200 || uploadResponse === 201;
};

const handleEvalUploadWhenEditing = async () => {
    return await uploadCoreDatasetIfNeeded();
};

const handleEvalUploadWhenCreating = async () => {
    const uploadEvalResponse = datasetType.value === 'eval' 
        ? await handleEvalFilesUpload() 
        : await handleEvalBuilderUpload();
    if (!uploadEvalResponse) return false;
    return await uploadCoreDatasetIfNeeded();
};

const runUploadsForType = async () => {
    if (datasetType.value === 'internal') {
        const uploadResponse = await startFileUpload();
        return uploadResponse === 200 || uploadResponse === 201;
    }

    if (datasetType.value === 'eval' || datasetType.value === 'eval-builder') {
        if (isEditing.value && evalId.value) {
            return await handleEvalUploadWhenEditing();
        }
        return await handleEvalUploadWhenCreating();
    }

    return true;
};

const sendDatasetConfirmation = async () => {
    if (!validateDatasetForm()) return;
    if (datasetType.value ==='internal' && !selectedFile.value) return;

    isSubmitting.value = true;
    if (isAdmin.value) {
        await handleAdminDatasetConfirm();
        return;
    }

    try {  
        const uploadsOk = await runUploadsForType();
        if (!uploadsOk) {
            isSubmitting.value = false;
            return;
        }

        const datasetInfo = prepareDatasetInfo();
        if (isEditing.value) {
            await handleDatasetUpdate(datasetInfo, datasetId.value);
        } else {
            await handleDatasetCreate(datasetInfo);
        }
    } catch (error) {
        console.error('Error creating/updating dataset:', error);
        addToast(error, 'error');
    } finally {
        isSubmitting.value = false;
    }
};

const handleAdminDatasetConfirm = async () => {
    try {
        const uploadsOk = await runUploadsForType();
        if (!uploadsOk) {
            isSubmitting.value = false;
            return;
        }

        if (isEditing.value) {
            await handleDatasetUpdate(prepareDatasetInfo(), datasetId.value);
        } else {
            await handleDatasetCreate(prepareDatasetInfo());
        }
    } catch (error) {
        console.error('Error creating/updating dataset:', error);
        addToast(error, 'error');
        isSubmitting.value = false;
    }
}

const handleAlertConfirm = () => {
    if (!datasetId.value) {
        addToast('Dataset ID not available', 'error');
        return;
    }
    
    router.push({ 
        name: 'dataset-listing-create', 
        params: { 
            datasetId: datasetId.value.toString()
        },
        query: {
            isEvalDataset: datasetType.value === 'eval' || datasetType.value === 'eval-builder'
        }
    });
};

const handleAlertCancel = () => {
    showAlert.value = false;
    router.push({ name: 'creator-profile', query: { tab: 'datasets' } });
};

const handleDeleteDataset = async () => {
    if (!datasetId.value) return;
    
    showDeleteAlert.value = true;
    alertTitle.value = 'Confirm Deletion';
    alertMessage.value = 'Are you sure you want to delete this dataset? All related listings will also be deleted. This action cannot be undone.';
    alertType.value = 'warning';
    alertShowPasswordInput.value = true;
};

const confirmDeleteDataset = async (data) => {
    if (!datasetId.value) return;
    isDeleting.value = true;
    try {
        const payload = {
            password: data?.password || null
        };
        
        const response = await deleteDataset(datasetId.value, payload);
        if (response.status === 200) {
            router.push({ name: 'creator-profile', query: { tab: 'datasets' } });
        }
    } catch (error) {
        console.error(error);
    } finally {
        showDeleteAlert.value = false;
        isDeleting.value = false;
    }
};

const cancelDeleteDataset = () => {
    showDeleteAlert.value = false;
};

const handleUpdateOwnerConfirm = () => {
    if (!isAdmin.value) {
        addToast('Unauthorized: Admin access required', 'error');
        return;
    }
    
    showUpdateOwnerAlert.value = true;
    alertTitle.value = 'Confirm Owner Update';
    alertMessage.value = `Are you sure you want to update the owner of this dataset from <span class="font-bold">${originalOwnerName.value}</span> to <span class="font-bold">${username.value}</span>? This action will change the dataset ownership.`;
    alertType.value = 'warning';
};

const confirmUpdateOwner = async () => {
    if (!isAdmin.value) {
        addToast('Unauthorized: Admin access required', 'error');
        return;
    }
    
    if (!validateOwnerUpdateForm()) {
        showUpdateOwnerAlert.value = false;
        return;
    }
    
    showUpdateOwnerAlert.value = false;
    isSubmitting.value = true;
    
    try {
        await handleDatasetUpdateOwner(datasetId.value, prepareDatasetInfo());
        originalOwner.value = selectedOwner.value;
        originalOwnerName.value = username.value;
    } catch (error) {
        console.error('Error updating dataset owner:', error);
        addToast(error, 'error');
        selectedOwner.value = username.value;
    } finally {
        isSubmitting.value = false;
    }
};

const cancelUpdateOwner = () => {
    showUpdateOwnerAlert.value = false;
    selectedOwner.value = originalOwner.value;
};

const handleOwnerChange = () => {
    if (!isAdmin.value || !isEditing.value) return;
    
    if (selectedOwner.value !== originalOwner.value) {
        handleUpdateOwnerConfirm();
    }
};

const pageTitle = computed(() => {
    return isEditing.value ? 'Edit Dataset' : 'Create a New Dataset';
});

const buttonText = computed(() => {
    return isEditing.value ? 'Update Dataset' : 'Create Dataset';
});

const datasetType = ref('internal');
const isLoadingConnectionTypes = ref(false);
const connectionType = ref(null);
const estimatedSize = ref(null);

const errorMessages = computed(() => ({
    datasetName: {
        message: datasetType.value === 'eval' 
            ? 'Core dataset name is required' 
            : 'Dataset name is required',
        label: datasetType.value === 'eval' 
            ? 'Core Dataset Name' 
            : 'Dataset Name'
    }
}));

const datasetNameError = computed(() => {
    if (!errors.value.datasetName) return '';
    return errorMessages.value.datasetName.message;
});

const sanitizeEstimatedSize = (value) => {
    if (!value || value === '') return '';
    
    let sanitized = String(value).replaceAll(/[^0-9.]/g, '');
    
    const parts = sanitized.split('.');
    if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('');
    }
    
    if (sanitized.startsWith('.')) {
        sanitized = '0' + sanitized;
    }
    
    return sanitized;
};

const handleEstimatedSizeInput = (event) => {
    const inputValue = event.target.value;
    const sanitized = sanitizeEstimatedSize(inputValue);
    
    if (sanitized === inputValue) {
        estimatedSize.value = sanitized;
    } else {
        event.target.value = sanitized;
        estimatedSize.value = sanitized;
    }
};

const handleEstimatedSizeKeydown = (event) => {
    const key = event.key;
    const currentValue = event.target.value;
    
    const controlKeys = [
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End', 'Tab', 'Enter', 'Escape'
    ];
    
    if (controlKeys.includes(key)) {
        return;
    }
    
    if (event.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(key.toLowerCase())) {
        return;
    }
    
    if (key === '.' && currentValue.includes('.')) {
        event.preventDefault();
        return;
    }
    
    if (key === '-') {
        event.preventDefault();
    }
};

const connectionTypeList = ref([]);

const toggleDatasetType = async (type) => {
    if (isEditingEvalDataset.value) {
        return;
    }

    allowedExtensions.value = type === 'eval' || type === 'eval-builder' ? ['.zip', '.tar.gz'] : ['.parquet', '.zip', '.tar.gz'];

    datasetType.value = type;
    if (type === 'external') {
        await getConnectionTypes();
    }
};

const handleCreateNewConnectionType = async (value) => {
    isLoadingConnectionTypes.value = true;
    const response = await axios.post('/api/external-sources', {
        name: value
    });
    if (response.status === 200 || response.status === 201) {
        connectionTypeList.value.push({ label: value, value: response.data.id });
        connectionType.value = response.data.id;
    }
    isLoadingConnectionTypes.value = false;
};

const getConnectionTypes = async () => {
    const response = await axios.get('/api/external-sources');
    if (response.status === 200) {
        connectionTypeList.value = response.data.map(item => ({ label: item.name, value: item.id }));
        connectionType.value = connectionTypeList.value.find(item => item.label === 'Other')?.value || null;
    }
};

const convertSizeTo = (size, unit = 'GB') => {
    const convertions = {
        'GB': () => size / 1024 / 1024 / 1024,
        'B': () => size * 1024 * 1024 * 1024,
    }
    return convertions[unit]();
}

const showEvalFormatModal = ref(false);
const openEvalFormatModal = () => {
    showEvalFormatModal.value = true;
}

const evalFormat = ref(`
Eval datasets on Portex use 4 parts. Two are required.
## **Upload Checklist**

1. **Required:** tasks.json
2. **Required:** answers.json
3. **Optional:** reference_files.zip
4. **Optional (but recommended for premium offering):** core_dataset.zip

**Rule: Every task_id in answers.json must also exist in tasks.json.**

## **🧩 Task List**

A JSON file containing a ${'`'}version${'`'} and a ${'`'}prompts${'`'} array. Each prompt must include:

- ${'`'}task_id${'`'}: unique identifier
- ${'`'}task_prompt${'`'}: the question for the model
- ${'`'}reference_file${'`'}: if your task requires a reference file, include the file name here.

**Example:**

\`\`\`
{
    "version": 2,
    "prompts": [
        {
            "task_id": "apple_net_margin_2024",
            "task_prompt": "Using Apple's FY 2024 10-K, compute its net profit margin (net income ÷ revenue).",
            "reference_file": "apple_2024_10K.txt"
        }
    ]
}
\`\`\`

This file will be downloadable by eval buyers so they can generate model responses.

---
## **🧠 Answer Keys**

A JSON file mapping ${'`'}task_id${'`'} to the correct output. Must include:

- ${'`'}task_id${'`'}: unique identifier used above
- ${'`'}answer${'`'}: the expected result (numerical, textual, objective criteria etc.)

Optional fields you might include to supplement:

- \`rationale\`: explanation of how the answer was derived, reasoning traces
- \`answer_type\`: e.g. "percentage", "multiple_choice", "exact_match"

**Example:**

\`\`\`
[
    {
    "task_id": "apple_net_margin_2024",
    "answer": 0.2397,
    "rationale": "Net income $93,736M ÷ Revenue $391,035M = 23.97%",
    "answer_type": "percentage"
    }
]
\`\`\`

By default, your answer key is **blinded** from eval buyers. You may optionally add it to the Core Dataset below for purchase.

---
## **📂 Reference Files (Optional)**

If your tasks refer to text documents or images or other supporting files, bundle them into a ${'`'}.zip${'`'} or ${'`'}.tar.gz${'`'} archive.

Example archive/zip structure:

\`\`\`
reference_files/
    apple_2024_10K.txt
    revenue_breakdown_chart.png
\`\`\`

Buyers will extract this archive to access the referenced files and generate model responses.

---
## **📘 Core Dataset (Optional)**

A single ${'`'}.zip${'`'} archive that may optionally contain the answer key and/or additional files that support your reasoning or calculations (analyst notes, data tables, or expert commentary).

Example archive structure:

\`\`\`
core_dataset/
    answers.json
    expert_notes.json
    margin_calculation_factors.json
\`\`\`

Buyers can use this core dataset to validate or audit the answer logic, or even refine their models using reinforcement learning methods. The contents of this dataset are up to you as the expert to decide but including the answer key can generally support a higher price point.`);
</script>

<template>
    <Toast />
    <Alert v-if="!isEditing && showAlert" 
        :show="showAlert" 
        :title="alertTitle" 
        :message="alertMessage" 
        :type="alertType" 
        confirmText="Yes, create listing"
        cancelText="No, back to Data Studio"
        @confirm="handleAlertConfirm" 
        @close="handleAlertCancel"
        :showCloseBtn="false"
        dataTestId="dataset-create-listing-alert"
    />
    <Alert
        v-if="showDeleteAlert"
        :show="showDeleteAlert" 
        :title="alertTitle" 
        :message="alertMessage" 
        :type="alertType" 
        :showPasswordInput="alertShowPasswordInput"
        passwordLabel="Confirm deletion"
        passwordPlaceholder="Type your password to confirm"
        passwordRequired
        @confirm="confirmDeleteDataset" 
        @close="cancelDeleteDataset"
        :showCloseBtn="false"
        :isLoading="isDeleting"
        loadingText="Deleting dataset..."
        confirmText="Delete Dataset"
    />
    <Alert
        v-if="showFileExistsAlert"
        :show="showFileExistsAlert"
        title="File Already Exists"
        :message="`A file named <strong>${existingFileName}</strong> already exists in the system. <br><br>If you want to use this file, please rename your upload or choose 'Use Existing File' to link to the existing one.`"
        type="warning"
        confirmText="Use Existing File"
        cancelText="Cancel"
        :showInput="false"
        :showCloseBtn="false"
        :errorMessage="fileExistsError"
        @confirm="handleFileExistsConfirm"
        @close="handleCancelFileExists"
    />
    <Alert
        v-if="showRemoveFileAlert"
        :show="showRemoveFileAlert"
        :title="alertTitle"
        :message="alertMessage"
        :type="alertType"
        confirmText="Yes, remove file"
        cancelText="Cancel"
        @confirm="confirmRemoveFile"
        @close="cancelRemoveFile"
        :showCloseBtn="false"
    />
    <Alert
        v-if="showUpdateOwnerAlert && isAdmin"
        :show="showUpdateOwnerAlert"
        :title="alertTitle"
        :message="alertMessage"
        :type="alertType"
        confirmText="Update Owner"
        cancelText="Cancel"
        @confirm="confirmUpdateOwner"
        @close="cancelUpdateOwner"
        :showCloseBtn="false"
        loadingText="Updating owner..."
    />

    <Modal :isOpen="showEvalFormatModal" @close="showEvalFormatModal = false" :title="`How to structure your Eval Dataset Bundle`" size="4xl">
        <div class="modal-body">
            <MarkdownRenderer v-model="evalFormat" :is-editing="false" :content-only="true" v-if="evalFormat" class="p-4"/>
        </div>
    </Modal>


    <div class="creator-profile__container overflow-hidden  h-full">
        <div class="flex sm:flex-col items-center justify-between">
            <div class="breadcrumb w-full">
                <router-link to="/creator-profile?page=datasets&subpage=edit-datasets" class="breadcrumb-link">
                    <svg class="breadcrumb-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd" />
                    </svg>
                    <span class="whitespace-nowrap text-sm">Back to Datasets</span>
                </router-link>
            </div>
            <div class="creator-profile__container-stepper" v-if="!isEditing">
                <Stepper :steps="steps" :currentStep="currentStep" />
            </div>
        </div>
        
        <div class="creator-profile__container-content w-full flex items-stretch h-[calc(100vh-200px)]">
            <div class="pr-10 w-[40%] pb-3 overflow-y-auto overflow-x-hidden gradient-divider">
                <div class="w-full flex flex-col items-center justify-center mb-8">
                    <h1 class="creator-profile__container-content-title">{{ pageTitle }}</h1>
                    <p v-if="!isEditing" class="text-sm 2xl:text-base text-gray-300 text-center">
                        <span>Upload your dataset or point to an external source.</span>
                    </p>
                    <p v-else class="text-sm 2xl:text-base text-gray-300 text-center">You are editing an existing dataset. You can update the dataset name, description, and upload a new .parquet or .tar.gz file if needed.</p>
                </div>

                <div class="w-full">
                    <div class="grid grid-cols-1 gap-4">
                        <div @click="toggleDatasetType('internal')" class="dataset-type-card" :class="[datasetType === 'internal' ? 'active' : 'inactive', isEditingEvalDataset ? 'disabled' : '']" data-testid="internal-dataset-card">
                            <i class="uil uil-upload dataset-type-card-icon" :class="{'active': datasetType === 'internal'}"></i>
                            <div>
                                <div class="dataset-type-card-title">Upload a File</div>
                                <div class="dataset-type-card-description">Upload a dataset directly to the PortexAi Datalab</div>
                            </div>
                        </div>
                        <div @click="toggleDatasetType('external')" class="dataset-type-card" :class="[datasetType === 'external' ? 'active' : 'inactive', isEditingEvalDataset ? 'disabled' : '']" data-testid="external-dataset-card">
                            <i class="uil uil-database dataset-type-card-icon" :class="{'active': datasetType === 'external'}"></i>
                            <div>
                                <div class="dataset-type-card-title">External Dataset</div>
                                <div class="dataset-type-card-description">List connection to an external data source</div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            
            <div class="pl-10 pr-3 w-1/2 pb-3 dataset-form-scroll-container">
                <div class="form-container">
                    <div class="w-full flex flex-col items-center justify-center mb-8" v-if="datasetType === 'internal'">
                        <h1 class="creator-profile__container-content-title">Update your Dataset</h1>
                    </div>
                    <div class="w-full flex flex-col items-center justify-center mb-8" v-if="datasetType === 'eval'">
                        <h1 class="creator-profile__container-content-title">Eval Dataset Bundle</h1>
                        <p class="text-sm 2xl:text-base text-gray-300 text-center">Eval datasets need to be structured following <button class="underline text-echoes-primary hover:text-echoes-primary/80" @click="openEvalFormatModal">this format.</button></p>
                    </div>
                    <div class="w-full flex flex-col items-center justify-center mb-8" v-if="datasetType === 'eval-builder'">
                        <h1 class="creator-profile__container-content-title">Write an Eval</h1>
                        <div class="flex items-center justify-center gap-2">
                            <p class="text-gray-300 text-sm 2xl:text-base text-center">
                            Write your eval and upload supporting files. Looking for design tips? Follow 
                            <a class="text-echoes-primary hover:text-echoes-primary/80 underline" href="https://docs.portexai.com/portex-docs/datalab/creating-an-eval/details" target="_blank">this guide</a> 
                            to writing good evals.
                            </p>
                    </div>
                    </div>
                    <div class="w-full flex flex-col items-center justify-center mb-8" v-if="datasetType === 'external'">
                        <h1 class="creator-profile__container-content-title">External Dataset Details</h1>
                        <div class="flex items-center justify-center gap-2">
                            <p class="text-sm 2xl:text-base text-gray-300 text-center">External dataset are verified at the time of purchase.</p>
                            <div class="text-white hover:text-echoes-primary transition-colors relative" 
                                    @mouseenter="showTooltip" 
                                    @mouseleave="hideTooltip"
                                    ref="tooltipTrigger">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="info-circle"
                                    class="w-5 h-5 cursor-help flex-shrink-0">
                                    <path fill="currentColor"
                                        d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z">
                                    </path>
                                </svg>
                            </div>
                        </div>
                    </div>
                
                    <Teleport to="body">
                        <div v-if="tooltipVisible" 
                                class="fixed z-[9999] px-3 py-2 text-sm text-gray-100 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[200px] pointer-events-none transition-all duration-200 ease-in-out"
                                :style="{ left: tooltipPosition.x + 'px', top: tooltipPosition.y + 'px', transform: 'translate(-50%, -100%)', marginTop: '-8px' }">
                            When a purchase occurs, an agent checks for data availability.
                        </div>
                    </Teleport>

                    <div class="form-fields">

                        <div class="field-input-group gap-4 sm:gap-0 flex-col sm:flex-row" :class="{'grid grid-cols-3 mb-4': datasetType === 'eval-builder'}">
                            <div class="field-input-group-item-badge" :class="{'w-full': datasetType === 'eval-builder'}">
                                <div class="mb-1">
                                    <div class="field-input-group-item-label-container">
                                        <div class="field-input-group-item-label">
                                        Owner
                                    </div>

                                    </div>
                                    <InputError :message="errors.seller" class="label-error" />
                                </div>
                                <!-- admin user dropdown -->
                                <div class="flex items-center w-full">
                                <template v-if="isAdmin">
                                    <div class="owner-select whitespace-nowrap flex-1">
                                    
                                        <SelectInput 
                                            id="owner" 
                                            dataTestId="dataset-owner-select"
                                            v-model="selectedOwner" 
                                            :options="ownersList"
                                            placeholder="Select owner"
                                            class="w-full"
                                            :showProfilePicture="true"
                                            :disabled="isUploading || isLoadingDataset || isSubmitting"
                                            :autocomplete="showAlert || showDeleteAlert || showFileExistsAlert || showRemoveFileAlert || showUpdateOwnerAlert ? 'off' : 'on'"
                                            @change="handleOwnerChange"
                                        @update:modelValue="handleOwnerChange"
                                        />
                                    </div>
                                </template>
                                <!-- regular users static badge -->
                                <template v-else>
                                    <div class="user-badge">
                                        <div class="user-avatar">
                                            <img v-if="profilePic" :src="profilePic" alt="User avatar" />
                                            <User v-else class="w-full h-full p-1 text-gray-100" />
                                        </div>
                                        <div class="whitespace-nowrap">{{ username }}</div>
                                    </div>
                                </template>
                                <div class="separator hidden lg:block">/</div>
                            </div>
                            </div>
                            
                            <div class="field-input-group-item-dataset-name w-full" :class="{'w-full': datasetType === 'eval-builder'}">
                                <div class="mb-1">
                                    <div class="field-input-group-item-label-container whitespace-nowrap">
                                        <div class="field-input-group-item-label" v-if="datasetType === 'eval' || datasetType === 'eval-builder'">Eval Dataset Name</div>
                                        <div class="field-input-group-item-label" v-else>Dataset Name</div>
                                    </div>
                                    <InputError :message="datasetNameError" class="label-error" />
                                </div>
                                
                                <TextInput    
                                    id="datasetName" 
                                    dataTestId="dataset-name-input"
                                    v-model="datasetName" 
                                    placeholder="Insert the dataset's name"
                                    :required="true" 
                                    class="w-full" 
                                    :disabled="isUploading || isLoadingDataset || isSubmitting"
                                    :autocomplete="showAlert || showDeleteAlert || showFileExistsAlert || showRemoveFileAlert || showUpdateOwnerAlert ? 'off' : 'on'"
                                />
                            </div>

                                <!-- Core Dataset (Optional) for Eval Builder -->

                            <div class=" text-base w-full font-medium text-white flex justify-center flex-col h-full pl-2" v-if="datasetType === 'eval-builder'">
                                <div class="mb-1">
                                    <div class="field-input-group-item-label-container">
                                        <div class="field-input-group-item-label">Core Dataset</div>
                                        <div class="text-echoes-primary hover:text-echoes-primary/80 transition-colors relative group">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                class="w-5 h-5 cursor-help flex-shrink-0">
                                                <path fill="currentColor"
                                                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                                                </path>
                                            </svg>
                                            <span class="eval-tooltip eval-tooltip-left">A .zip or .tar.gz archive containing additional files that support your reasoning or calculations (references, analyst notes, data tables, or expert commentary).</span>
                                        </div>
                                    </div>
                                </div>
                                <label for="coreDatasetFile" class="flex items-center justify-between px-4 py-2.5 rounded-[13px] h-11 bg-echoes-background-black border border-echoes-border-gray hover:bg-echoes-background-black hover:border-white/20 hover:text-white focus:border-echoes-primary transition-all duration-200 cursor-pointer w-full"
                                    :class="{ 'text-white': selectedFile, 'text-white/50': !selectedFile }">
                                    <div class="flex items-center gap-2 flex-1 pr-2 overflow-hidden">
                                        <i class="uil text-sm"
                                            :class="selectedFile ? 'uil-file' : 'uil-upload'"></i>
                                        <span class="text-sm flex items-center truncate">
                                            {{ selectedFile ? selectedFile.name : 'Upload (Optional)' }}
                                        </span>
                                    </div>
                                    <div class="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            v-if="selectedFile"
                                            type="button"
                                            class="rounded-full hover:bg-echoes-background-gray/50 transition-colors duration-150"
                                            @click.stop="handleClearFile($event)"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 text-white/80">
                                                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                                            </svg>
                                        </button>
                                    </div>
                                </label>
                                <input 
                                    type="file" 
                                    id="coreDatasetFile" 
                                    @change="handleFileSelect" 
                                    class="hidden"
                                    :disabled="isUploading || isLoadingDataset || isSubmitting"
                                    accept=".parquet,.zip,.tar.gz"
                                />

                            </div>
                            </div>
                            
                        

                        <div class="field-input-group-item-full" v-if="datasetType === 'internal'">
                            <div class="mb-1">
                                <div class="field-input-group-item-label-container w-fit">
                                    <div class="field-input-group-item-label">Init Commit</div>
                                    <div class="character-counter">{{ initCommit.length }}/300</div>
                                </div>
                                <InputError :message="errors.initCommit" class="label-error" />
                            </div>
                            <div class="textarea-container">
                                <textarea 
                                    id="initCommit" 
                                    data-testid="dataset-init-commit-input"
                                    v-model="initCommit" 
                                    placeholder="Max length 300 characters."
                                    class="custom-textarea" 
                                    maxlength="300" 
                                    required
                                    :disabled="isUploading || isLoadingDataset || isSubmitting"
                                    :autocomplete="showAlert || showDeleteAlert || showFileExistsAlert || showRemoveFileAlert || showUpdateOwnerAlert ? 'off' : 'on'"
                                ></textarea>
                            </div>
                        </div>
                        
                        <div class="field-input-group gap-4 flex-col sm:flex-row mt-6" v-if="datasetType === 'external'">
                            <div class="field-input-group-item-badge w-full">
                                <div class="mb-1">
                                    <div class="field-input-group-item-label-container">
                                        <div class="field-input-group-item-label">Connection type</div>
                                    </div>
                                    <InputError :message="errors.connectionType" class="label-error" />
                                </div>
                                <div class="owner-select whitespace-nowrap w-full z-50">
                                    <ComboboxInput 
                                        id="connectionType" 
                                        v-model="connectionType" 
                                        :options="connectionTypeList"
                                        placeholder="Select connection type"
                                        class="w-full"
                                        :allowCreate="true"
                                        :disabled="isUploading || isLoadingDataset || isSubmitting"
                                        @create-new="handleCreateNewConnectionType"
                                        :loading="isLoadingConnectionTypes"
                                        :required="true"
                                    />
                                </div>
                            </div>
                            
                            <div class="field-input-group-item-dataset-name w-full">
                                <div class="mb-1">
                                    <div class="field-input-group-item-label-container">
                                        <div class="field-input-group-item-label">Estimated size (optional)</div>
                                    </div>
                                    <InputError :message="errors.estimatedSize" class="label-error" />
                                </div>
                                <TextInput 
                                    id="estimatedSize" 
                                    data-testid="dataset-estimated-size-input"
                                    v-model="estimatedSize" 
                                    placeholder="Enter estimated size in GB"
                                    :required="false"
                                    :type="'number'"
                                    class="w-full"
                                    min="0"
                                    step="0.01"
                                    @input="handleEstimatedSizeInput"
                                    @keydown="handleEstimatedSizeKeydown"
                                />
                            </div>
                        </div>

                    </div>

                    <div class="file-upload-container" v-if="datasetType === 'internal'">
                        <div class="format-notice">
                            <p>You must upload files using either the .parquet or .tar.gz formats</p>
                        </div>
                        <div class="file-upload-area" 
                            :class="{ 
                                'dragging': isDragging && !isLoadingDataset && !isUploading,
                                'disabled': isLoadingDataset || isUploading
                            }" 
                            @dragleave="handleDragLeave"
                            @dragover="handleDragOver" 
                            @drop="handleDrop"
                            @dragenter.prevent>
                            <div class="upload-content">
                                <div class="upload-icon">
                                    <svg v-if="!selectedFile" class="w-10 h-10 2xl:w-12 2xl:h-12" viewBox="0 0 94 94" 
                                        fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                            stroke="#A7ACB9" stroke-width="6" stroke-linecap="round"
                                            stroke-linejoin="round" />
                                    </svg>
                                    <svg v-else class="w-10 h-10 2xl:w-12 2xl:h-12 relative" aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                        viewBox="0 0 24 24">
                                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M13.213 9.787a3.391 3.391 0 0 0-4.795 0l-3.425 3.426a3.39 3.39 0 0 0 4.795 4.794l.321-.304m-.321-4.49a3.39 3.39 0 0 0 4.795 0l3.424-3.426a3.39 3.39 0 0 0-4.794-4.795l-1.028.961" />
                                    </svg>
                                    <div v-if="isUploading && uploadProgress >= 100 && !hasUploadError" class="check-icon-container">
                                        <svg class="w-6 h-6 check-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" 
                                            width="24" height="24" fill="none" viewBox="0 0 24 24">
                                            <path stroke="#10B981" stroke-linecap="round" stroke-linejoin="round" 
                                                stroke-width="2" d="M5 11.917 9.724 16.5 19 7.5"/>
                                        </svg>
                                    </div>
                                    <div v-if="hasUploadError" class="error-icon-container">
                                        <svg class="w-6 h-6 error-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" 
                                            width="24" height="24" fill="none" viewBox="0 0 24 24">
                                            <path stroke="#EF4444" stroke-linecap="round" stroke-linejoin="round" 
                                                stroke-width="2" d="M6 18 17.94 6M18 18 6.06 6"/>
                                        </svg>
                                    </div>
                                </div>
                                
                                <div v-if="isUploading" class="upload-text upload-text-progress">
                                    <div class="file-name-container">
                                        <p class="file-name-uploading">{{ selectedFile.name }}</p>
                                        <span class="file-size-uploading">{{ formatFileSize(selectedFile.size) }}</span>
                                    </div>
                                    <div class="progress-bar-container">
                                        <div class="progress-bar" :style="{ width: `${uploadProgress}%` }"></div>
                                    </div>
                                    <div class="progress-percentage">{{ Math.round(uploadProgress) }}%</div>
                                </div>
                                
                                <div v-else-if="hasUploadError" class="upload-text upload-text-error">
                                    <div class="file-name-container">
                                        <p class="file-name-error">{{ selectedFile ? selectedFile.name : 'Error uploading file' }}</p>
                                        <span v-if="selectedFile" class="file-size-uploading">{{ formatFileSize(selectedFile.size) }}</span>
                                    </div>
                                    <div class="error-actions">
                                        <button type="button" @click="removeSelectedFile" class="cancel-upload-btn" 
                                            :disabled="isSubmitting || isLoadingDataset"
                                            :class="{ 'disabled': isSubmitting || isLoadingDataset }"
                                            aria-label="Remove file">
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                                
                                <div v-else-if="selectedFile" class="upload-text">
                                    <div class="file-details">
                                        <div class="file-info">
                                            <div class="file-name">{{ selectedFile.name }}</div>
                                            <div class="file-info-bottom">
                                                <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
                                                <button @click="removeSelectedFile" class="remove-file-text" 
                                                    :disabled="isSubmitting || isLoadingDataset"
                                                    :class="{ 'disabled': isSubmitting || isLoadingDataset }"
                                                    aria-label="Remove file">
                                                    Remove file
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div v-else class="upload-text">
                                    <p>Drag and drop your file here or</p>
                                    <input type="file" 
                                        id="fileInput" 
                                        data-testid="dataset-file-input"
                                        @change="handleFileSelect" 
                                        class="hidden"
                                        :disabled="isLoadingDataset || isUploading || isSubmitting"
                                        :autocomplete="showAlert || showDeleteAlert || showFileExistsAlert || showRemoveFileAlert || showUpdateOwnerAlert ? 'off' : 'on'"
                                        :accept="allowedExtensions.join(',')" />
                                    <div class="mb-1 flex flex-col items-center">
                                        <label 
                                            for="fileInput" 
                                            class="choose-file-btn"
                                            :class="{ 'disabled': isLoadingDataset || isUploading || isSubmitting }"
                                            :tabindex="isLoadingDataset || isUploading || isSubmitting ? -1 : 0"
                                        >Choose file</label>
                                        <InputError :message="errors.file" class="mt-2" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Eval Builder Section -->
                    <EvalBuilder 
                        ref="evalBuilderRef"
                        v-if="datasetType === 'eval-builder'"
                        :selectedFile="selectedFile"
                        :isUploading="isUploading"
                        :isLoadingDataset="isLoadingDataset"
                        :isSubmitting="isSubmitting"
                        @update:selectedFile="selectedFile = $event"
                        @openFormatModal="openEvalFormatModal"
                    />

                    <div class="file-upload-container" v-if="datasetType === 'eval'">
                        <div class="eval-upload-sections">
                            <!-- 1. Task List -->
                            <div class="eval-upload-section">
                                <div class="eval-section-header">
                                    <div class="eval-section-title">
                                        <span class="eval-section-number">1.</span>
                                        <span class="eval-section-name">Task List</span>
                                        <div class="text-echoes-primary hover:text-echoes-primary/80 transition-colors relative group">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                class="w-5 h-5 cursor-help flex-shrink-0">
                                                <path fill="currentColor"
                                                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                                                </path>
                                            </svg>
                                            <span class="eval-tooltip eval-tooltip-right">JSON file containing your tasks. Each record must contain a task_id, task_prompt, and reference_file (null or empty string if not applicable).</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="eval-upload-area" 
                                    :class="{ 
                                        'disabled': evalFilesIsUploading || isUploading || isEditingEvalDataset,
                                        'dragging': isDragging && !evalFilesIsUploading && !isUploading && !isEditingEvalDataset
                                    }"
                                    @dragleave="handleEvalDragLeave"
                                    @dragover="handleEvalDragOver" 
                                    @drop="handleTaskFileDrop"
                                    @dragenter.prevent>
                                    <!-- Disabled state message -->
                                    <div v-if="isEditingEvalDataset" class="eval-disabled-message">
                                        <p>Cannot modify file in edit mode</p>
                                    </div>
                                    
                                    <!-- Normal upload content -->
                                    <div class="eval-upload-content" v-if="!taskFileSelected && !isEditingEvalDataset">
                                        <div class="eval-select-file-btn">
                                            <svg class="w-5 h-5" viewBox="0 0 94 94" 
                                                fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                    stroke="#A7ACB9" stroke-width="6" stroke-linecap="round"
                                                    stroke-linejoin="round" />
                                            </svg>
                                            <span>Select File</span>
                                        </div>
                                        <div class="eval-upload-text">
                                            <p>Drop your task list file here, or click on Select File</p>
                                        </div>
                                    </div>
                                    <input type="file" id="taskFileInput" @change="handleTaskFileSelect" class="hidden" :disabled="evalFilesIsUploading || isUploading || isSubmitting || isEditingEvalDataset" accept=".json,.txt" />
                                    <label for="taskFileInput" class="eval-file-input-label" :class="{ 'disabled': evalFilesIsUploading || isUploading || isSubmitting || isEditingEvalDataset || taskFileSelected }" v-if="!taskFileSelected && !isEditingEvalDataset" aria-label="Select task file"></label>
                                    
                                    <!-- File selected state -->
                                    <div v-if="taskFileSelected" class="eval-file-selected">
                                        <div class="eval-file-info">
                                            <div class="eval-file-name">{{ taskFileSelected.name }}</div>
                                            <div class="eval-file-size">{{ formatFileSize(taskFileSelected.size) }}</div>
                                        </div>
                                        <button v-if="!isEditingEvalDataset" @click="removeTaskFileSelected" class="eval-remove-file" :disabled="isSubmitting || isLoadingDataset">Remove</button>
                                    </div>
                                </div>
                            </div>

                                    <!-- 2. Answer Keys -->
                                    <div class="eval-upload-section">
                                <div class="eval-section-header">
                                    <div class="eval-section-title">
                                        <span class="eval-section-number">2.</span>
                                        <span class="eval-section-name">Answer Keys</span>
                                        <div class="text-echoes-primary hover:text-echoes-primary/80 transition-colors relative group">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                class="w-5 h-5 cursor-help flex-shrink-0">
                                                <path fill="currentColor"
                                                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                                                </path>
                                            </svg>
                                            <span class="eval-tooltip">JSON file mapping each task_id to its answer. Must include task_id and answer fields. Always hidden unless included in your Core Dataset.</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="eval-upload-area" 
                                    :class="{ 
                                        'disabled': evalFilesIsUploading || isUploading || isEditingEvalDataset,
                                        'dragging': isDragging && !evalFilesIsUploading && !isUploading && !isEditingEvalDataset
                                    }"
                                    @dragleave="handleEvalDragLeave"
                                    @dragover="handleEvalDragOver" 
                                    @drop="handleAnswerFileDrop"
                                    @dragenter.prevent>
                                    <!-- Disabled state message -->
                                    <div v-if="isEditingEvalDataset" class="eval-disabled-message">
                                        <p>Cannot modify file in edit mode</p>
                                    </div>
                                    
                                    <!-- Normal upload content -->
                                    <div class="eval-upload-content" v-if="!answerFileSelected && !isEditingEvalDataset">
                                        <div class="eval-select-file-btn">
                                            <svg class="w-5 h-5" viewBox="0 0 94 94" 
                                                fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                    stroke="#A7ACB9" stroke-width="6" stroke-linecap="round"
                                                    stroke-linejoin="round" />
                                            </svg>
                                            <span>Select File</span>
                                        </div>
                                        <div class="eval-upload-text">
                                            <p>Drop your answer key file here, or click on Select File</p>
                                        </div>
                                    </div>
                                    <input type="file" id="answerFileInput" @change="handleAnswerFileSelect" class="hidden" :disabled="evalFilesIsUploading || isUploading || isSubmitting || isEditingEvalDataset" accept=".json,.txt" />
                                    <label for="answerFileInput" class="eval-file-input-label" :class="{ 'disabled': evalFilesIsUploading || isUploading || isSubmitting || isEditingEvalDataset || answerFileSelected }" v-if="!answerFileSelected && !isEditingEvalDataset" aria-label="Select answer file"></label>
                                    
                                    <!-- File selected state -->
                                    <div v-if="answerFileSelected" class="eval-file-selected">
                                        <div class="eval-file-info">
                                            <div class="eval-file-name">{{ answerFileSelected.name }}</div>
                                            <div class="eval-file-size">{{ formatFileSize(answerFileSelected.size) }}</div>
                                        </div>
                                        <button v-if="!isEditingEvalDataset" @click="removeAnswerFileSelected" class="eval-remove-file" :disabled="isSubmitting || isLoadingDataset">Remove</button>
                                    </div>
                                </div>
                            </div>

                            <!-- 3. Reference Files (Optional) -->
                            <div class="eval-upload-section">
                                <div class="eval-section-header">
                                    <div class="eval-section-title">
                                        <span class="eval-section-number">3.</span>
                                        <span class="eval-section-name">Reference Files {{ referenceFileIsRequired ? '(Required)' : '(Optional)' }}</span>
                                        <div class="text-echoes-primary hover:text-echoes-primary/80 transition-colors relative group">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                class="w-5 h-5 cursor-help flex-shrink-0">
                                                <path fill="currentColor"
                                                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                                                </path>
                                            </svg>
                                            <span class="eval-tooltip">Upload a <strong>.zip or .tar.gz</strong> archive containing your reference text or image files.All files must be at the top level <strong>(no folders)</strong>.For tar, run from inside your refs folder. <br> Example: <code>cd refs && tar -czf ../refs.tar.gz *</code></span>
                                        </div>
                                    </div>
                                </div>
                                <div class="eval-upload-area" 
                                    :class="{ 
                                        'disabled': evalFilesIsUploading || isUploading || isEditingEvalDataset,
                                        'dragging': isDragging && !evalFilesIsUploading && !isUploading && !isEditingEvalDataset
                                    }"
                                    @dragleave="handleEvalDragLeave"
                                    @dragover="handleEvalDragOver" 
                                    @drop="handleReferenceFileDrop"
                                    @dragenter.prevent>
                                    <!-- Disabled state message -->
                                    <div v-if="isEditingEvalDataset" class="eval-disabled-message">
                                        <p>Cannot modify file in edit mode</p>
                                    </div>
                                    
                                    <!-- Normal upload content -->
                                    <div class="eval-upload-content" v-if="!referenceFileSelected && !isEditingEvalDataset && !isValidatingReferenceFile">
                                        <div class="eval-select-file-btn">
                                            <svg class="w-5 h-5" viewBox="0 0 94 94" 
                                                fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                    stroke="#A7ACB9" stroke-width="6" stroke-linecap="round"
                                                    stroke-linejoin="round" />
                                            </svg>
                                            <span>Select File</span>
                                        </div>
                                        <div class="eval-upload-text">
                                            <p>Drop your reference file here, or click on Select File</p>
                                        </div>
                                    </div>
                                    <input type="file" id="referenceFileInput" @change="handleReferenceFileSelect" class="hidden" :disabled="evalFilesIsUploading || isUploading || isSubmitting || isEditingEvalDataset || isValidatingReferenceFile" accept=".zip,.tar.gz" />
                                    <label for="referenceFileInput" class="eval-file-input-label" :class="{ 'disabled': evalFilesIsUploading || isUploading || isSubmitting || isEditingEvalDataset || referenceFileSelected }" v-if="!referenceFileSelected && !isEditingEvalDataset" aria-label="Select reference file"></label>
                                    <LoaderWithText v-if="isValidatingReferenceFile"
                                        :alignment="'inline'"
                                        :current-step="validatingReferenceFileStep"
                                        :steps-config="validateReferenceFileStepConfig"
                                        />
                                    
                                    <!-- File selected state -->
                                    <div v-if="referenceFileSelected" class="eval-file-selected">
                                        <div class="eval-file-info">
                                            <div class="eval-file-name">{{ referenceFileSelected.name }}</div>
                                            <div class="eval-file-size">{{ formatFileSize(referenceFileSelected.size) }}</div>
                                        </div>
                                        <button v-if="!isEditingEvalDataset" @click="removeReferenceFile" class="eval-remove-file" :disabled="isSubmitting || isLoadingDataset">Remove</button>
                                    </div>
                                </div>
                            </div>

                        

                            <!-- 4. Core Dataset (Optional) -->
                            <div class="eval-upload-section">
                                <div class="eval-section-header">
                                    <div class="eval-section-title">
                                        <span class="eval-section-number">4.</span>
                                        <span class="eval-section-name">Core Dataset (Optional)</span>
                                        <div class="text-echoes-primary hover:text-echoes-primary/80 transition-colors relative group">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                class="w-5 h-5 cursor-help flex-shrink-0">
                                                <path fill="currentColor"
                                                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                                                </path>
                                            </svg>
                                            <span class="eval-tooltip">.zip or .tar.gz archive containing knowledge files or reasoning traces that complement your answers. This file may optionally include the answer key file.</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="eval-upload-area" 
                                    :class="{ 
                                        'disabled': evalFilesIsUploading || isUploading,
                                        'dragging': isDragging && !evalFilesIsUploading && !isUploading
                                    }"
                                    @dragleave="handleEvalDragLeave"
                                    @dragover="handleEvalDragOver" 
                                    @drop="handleKnowledgeFileDrop"
                                    @dragenter.prevent>
                                    <div class="eval-upload-content" v-if="!selectedFile">
                                        <div class="eval-select-file-btn">
                                            <svg class="w-5 h-5" viewBox="0 0 94 94" 
                                                fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                    stroke="#A7ACB9" stroke-width="6" stroke-linecap="round"
                                                    stroke-linejoin="round" />
                                            </svg>
                                            <span>Select File</span>
                                        </div>
                                        <div class="eval-upload-text">
                                            <p>Drop your expert reference list file here, or click on Select File</p>
                                        </div>
                                    </div>
                                    <input type="file" id="knowledgeFileInput" @change="handleFileSelect" class="hidden" :disabled="evalFilesIsUploading || isUploading || isSubmitting" accept=".zip,.tar.gz" />
                                    <label for="knowledgeFileInput" class="eval-file-input-label" :class="{ 'disabled': evalFilesIsUploading || isUploading || isSubmitting || selectedFile }" v-if="!selectedFile" aria-label="Select knowledge reference file"></label>
                                    
                                    <!-- File selected state -->
                                    <div v-if="selectedFile" class="eval-file-selected">
                                        <div class="eval-file-info">
                                            <div class="eval-file-name">{{ selectedFile.name }}</div>
                                            <div class="eval-file-size">{{ formatFileSize(selectedFile.size) }}</div>
                                        </div>
                                        <button @click="selectedFile = null" class="eval-remove-file" :disabled="isSubmitting || isLoadingDataset">Remove</button>
                                    </div>
                                </div>
                            </div>

                            <!-- Progress indicator -->
                            <div v-if="evalFilesIsUploading || isUploading" class="eval-upload-progress">
                                <div class="progress-bar-container">
                                    <div class="progress-bar" :style="{ width: `${evalFilesUploadProgress}%` }"></div>
                                </div>
                                <div class="progress-percentage">{{ Math.round(evalFilesUploadProgress) }}%</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="action-container">
                    <div class="action-buttons">
                        <button 
                            v-if="isEditing" 
                            class="delete-dataset-btn hidden sm:block" 
                            @click="handleDeleteDataset"
                            :disabled="isSubmitting || isUploading || isLoadingDataset || isValidatingReferenceFile"
                        >
                            Delete Dataset
                        </button>
                        <button 
                            class="create-dataset-btn hidden sm:block" 
                            data-testid="dataset-submit-button"
                            @click="sendDatasetConfirmation" 
                            :disabled="isSubmitting || isUploading || isLoadingDataset || isValidatingReferenceFile"
                        >
                            <ButtonLoading 
                                v-if="isSubmitting || isLoadingDataset" 
                                :text="isLoadingDataset ? 'Loading...' : isUploading ? 'Uploading...' : isEditing ? 'Updating...' : 'Creating...'" 
                                size="sm" 
                            />
                            <span v-else>{{ buttonText }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.creator-profile__container {
    @apply relative max-w-7xl mx-auto h-full;
}

.creator-profile__container-stepper {
    @apply flex flex-col items-end sm:items-center justify-center mt-5 mb-10;
}

.creator-profile__container-content {
    @apply h-full flex justify-center;
}

.creator-profile__container-content-title {
    @apply text-3xl font-bold bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit;
}

.form-container {
    @apply flex flex-col w-full;
}

.form-fields {
    @apply space-y-6 flex flex-col h-full;
}


.field-input-group {
    @apply flex;
}

.field-input-group-item-badge {
    @apply text-base font-medium text-white flex justify-center flex-col h-full;
}

.field-input-group-item-dataset-name {
    @apply text-base w-1/2 font-medium text-white flex justify-center flex-col h-full;
}


.field-input-group-item-full {
    @apply text-base w-full font-medium text-white flex justify-center flex-col h-full;
}

.field-input-group-item-label {
    @apply text-sm 2xl:text-base;
}

.field-input-group-item-label-container {
    @apply flex items-center w-fit gap-2;
}

.label-error {
    @apply text-sm text-red-500;
}

.user-badge {
    @apply flex items-center gap-2 px-4 py-2 2xl:px-5 text-base rounded-2xl bg-echoes-background-gray/30 text-white border border-echoes-border-gray placeholder-white/70;
}

.user-avatar {
    @apply w-6 h-6 rounded-full overflow-hidden bg-echoes-primary;
}

.user-avatar img {
    @apply w-full h-full object-cover;
}

.separator {
    @apply mx-2 text-gray-400 text-3xl self-center flex-shrink-0;
}

.owner-select {
    @apply w-full;
}

.file-upload-container {
    @apply flex flex-col h-full mt-6;
}

.file-upload-area {
    @apply flex flex-col items-center justify-center border-2 border-dashed border-echoes-border-gray rounded-[13px] bg-echoes-background-black w-full h-full py-8 transition-all duration-200;
}

.file-upload-area.dragging:not(.disabled) {
    @apply border-echoes-primary bg-echoes-background-black;
}

.file-upload-area.disabled {
    @apply cursor-not-allowed;
    pointer-events: none;
}

.upload-content {
    @apply flex items-center justify-center;
}

.upload-icon {
    @apply text-gray-500 mr-4 relative;
}

.upload-text {
    @apply text-gray-400 text-sm sm:text-base flex flex-col items-center;
}

.file-details {
    @apply flex items-center justify-between w-full px-2;
}

.file-info {
    @apply flex flex-col items-start w-full;
}

.file-info-bottom {
    @apply flex items-center mt-1;
}

.file-name {
    @apply text-white font-medium;
}

.file-size {
    @apply text-white/80 text-sm;
}

.remove-file-text {
    @apply text-sm text-red-500 hover:text-red-600 transition-all duration-200 underline ml-4 cursor-pointer;
}

.remove-file-btn {
    @apply w-8 h-8 flex items-center justify-center cursor-pointer ml-5 text-base bg-red-700 text-white rounded-full hover:bg-red-600 transition;
}

.format-notice {
    @apply text-sm 2xl:text-base px-4 py-2 mb-3 bg-blue-500/20 border border-blue-500/50 text-blue-500 rounded-[13px] text-center break-words whitespace-normal;
    max-width: 100%;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.choose-file-btn {
    @apply w-fit cursor-pointer mt-2 2xl:text-base text-sm px-4 2xl:px-6 py-1.5 bg-echoes-background-gray text-white rounded-md hover:bg-echoes-background-gray/50 transition;
}

.action-container {
    @apply flex justify-center md:justify-end;
}

.action-buttons {
    @apply flex gap-4 items-center;
}

.delete-dataset-btn {
    @apply px-6 2xl:px-8 py-2 my-6 2xl:text-base text-sm  underline text-red-500 hover:text-red-600 transition duration-200 flex items-center justify-center min-w-[160px] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:text-red-500;
}

.create-dataset-btn {
    @apply px-6 2xl:px-8 py-2 my-6 2xl:text-base text-sm  bg-gradient-to-br from-green-500 to-green-700 text-white font-semibold rounded-full hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 transition duration-200 flex items-center justify-center min-w-[160px] disabled:opacity-70 disabled:cursor-not-allowed disabled:from-green-500/60 disabled:to-green-700/60;
}

.textarea-container {
    @apply relative h-[120px] sm:h-full text-sm;
}

.custom-textarea {
    @apply w-full h-full px-4 py-3 bg-echoes-background-black text-white border border-echoes-border-gray rounded-[13px] placeholder-white/70 focus:outline-none  focus:border-echoes-primary resize-none transition-all duration-200;

    &::placeholder {
        @apply transition-all duration-200 text-white/50 hover:text-white/80;
    }

    &:hover {
        @apply bg-echoes-background-black
        border-white/20
        text-white
        ;
    }

    &:focus::placeholder {
        @apply opacity-0 translate-x-1;
    }   

    &:focus {
        @apply border-echoes-primary;
    }

    &:disabled {
        @apply cursor-not-allowed opacity-60 bg-echoes-background-gray/30;
    }
}

.character-counter {
    @apply text-sm text-white/80 ml-auto mr-1;
}

.file-name-uploading {
    @apply text-white font-medium;
}

.file-name-container {
    @apply flex items-center gap-2 mb-2;
}

.file-size-uploading {
    @apply text-sm text-white/80 font-medium;
}

.upload-text-progress {
    @apply flex flex-col items-center w-full;
}

.progress-percentage {
    @apply text-sm text-white/80 mt-1 font-medium;
}

.progress-bar-container {
    @apply relative w-full h-2.5 bg-echoes-background-gray/30 rounded-full overflow-hidden 
    border border-echoes-border-gray shadow-inner;
}

.progress-bar {
    @apply h-full rounded-full transition-all duration-300 ease-out;
    background: linear-gradient(90deg, #FE3E01 0%, #FE3E01 100%);
    box-shadow: 0 0 10px #FE3E01;
}

.check-icon-container {
    @apply absolute bottom-0 right-0;
    z-index: 10;
}

.check-icon {
    @apply bg-white rounded-full p-0.5;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.error-icon-container {
    @apply absolute bottom-0 right-0;
    z-index: 10;
}

.error-icon {
    @apply bg-white rounded-full p-0.5;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.file-name-error {
    @apply text-red-500 font-medium;
}

.upload-text-error {
    @apply flex flex-col items-center w-full;
}

.error-actions {
    @apply flex justify-center;
}

.cancel-upload-btn {
    @apply px-4 py-1.5 text-sm text-white bg-echoes-background-gray rounded-md hover:bg-echoes-background-gray/50 transition;
}

.breadcrumb {
    @apply w-fit mt-4;
}

.breadcrumb-link {
    @apply flex underline items-center gap-1 text-white/80 hover:text-echoes-primary transition-colors duration-200 text-sm 2xl:text-base;
}

.breadcrumb-icon {
    @apply w-4 h-4 -ml-1;
}

.choose-file-btn.disabled {
    @apply opacity-50 cursor-not-allowed bg-echoes-background-gray hover:bg-echoes-background-gray/50;
    pointer-events: none;
}

.remove-file-text.disabled {
    @apply opacity-50 cursor-not-allowed hover:text-red-500;
    pointer-events: none;
}

.dataset-type-card {
    @apply flex items-center rounded-2xl border py-5 2xl:py-6 px-3 bg-echoes-background-gray/50 cursor-pointer transition-all duration-300 gap-3;
}

.dataset-type-card:hover:not(.disabled) {
    @apply bg-echoes-background-gray/70;
}

.dataset-type-card.inactive {
    @apply border-echoes-border-gray;
}

.dataset-type-card.active {
    @apply border-echoes-primary;
}

.dataset-type-card.disabled {
    @apply opacity-50 cursor-not-allowed pointer-events-none;
}

.dataset-type-card-icon {
    @apply text-4xl flex items-center justify-center w-10 flex-shrink-0;
}

.dataset-type-card-icon.active {
    @apply text-echoes-primary;
}

.dataset-type-card-title{
    @apply text-base 2xl:text-lg font-medium text-white;
}

.dataset-type-card-description{
    @apply text-sm text-gray-400;
}

.eval-tooltip {
    @apply absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-sm text-gray-100 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[300px] border-0;
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    z-index: 50;
    pointer-events: none;
}

.group:hover .eval-tooltip {
    visibility: visible;
    opacity: 1;
}

.eval-tooltip-right {
    left: calc(50% + 1.5rem);
    transform: translateX(-50%);
}

.eval-tooltip-left {
    left: 0%;
    transform: translateX(-90%);
}

.gradient-divider {
    position: relative;
}

.gradient-divider::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to top, transparent 0%, #232323 100%);
}

.dataset-form-scroll-container {
    overflow-y: auto;
    position: relative;
}

.dataset-form-scroll-container > .form-container {
    padding-top: 60px;
    margin-top: -60px;
}

/* Eval Upload Styles */
.eval-upload-sections {
    @apply space-y-6 mt-2;
}

.eval-upload-section {
    @apply flex flex-col;
}

.eval-section-header {
    @apply mb-2;
}

.eval-section-title {
    @apply flex items-center gap-2;
}

.eval-section-number {
    @apply font-medium text-white;
}

.eval-section-name {
    @apply font-medium text-white text-sm 2xl:text-base;
}

.eval-help-icon {
    @apply cursor-help;
}

.eval-upload-area {
    @apply relative border-2 border-dashed border-echoes-border-gray rounded-lg bg-echoes-background-black transition-all duration-200 p-4;
}

.eval-upload-area.disabled {
    @apply cursor-not-allowed opacity-50;
    pointer-events: none;
}

.eval-upload-area.dragging {
    @apply border-echoes-primary bg-echoes-background-black;
}

.eval-upload-content {
    @apply flex items-center gap-4;
}

.eval-select-file-btn {
    @apply flex items-center gap-2 px-2 py-1 border-2 border-echoes-background-gray text-white rounded-lg transition cursor-pointer text-sm 2xl:text-base;
}

.eval-upload-text {
    @apply flex-1;
}

.eval-upload-text p {
    @apply text-sm 2xl:text-base text-gray-400;
}

.eval-file-input-label {
    @apply absolute inset-0 cursor-pointer;
}

.eval-file-input-label.disabled {
    @apply cursor-not-allowed;
    pointer-events: none;
}

.eval-file-selected {
    @apply flex items-center justify-between p-3;
}

.eval-file-info {
    @apply flex flex-col;
}

.eval-file-name {
    @apply text-white font-medium text-base;
}

.eval-file-size {
    @apply text-gray-400 text-sm;
}

.eval-remove-file {
    @apply text-sm text-red-500 hover:text-red-600 transition-colors underline cursor-pointer;
}

.eval-remove-file:disabled {
    @apply opacity-50 cursor-not-allowed;
}

.eval-upload-progress {
    @apply mt-4 p-4 bg-echoes-background-gray/30 rounded-md;
}

.eval-disabled-message {
    @apply flex items-center justify-center;
}

.eval-disabled-message p {
    @apply text-base text-gray-400 text-center;
}

.core-dataset-upload-btn {
    @apply inline-flex items-center gap-2 px-4 py-2 bg-echoes-background-gray/50 border border-echoes-border-gray text-white rounded-xl cursor-pointer hover:bg-echoes-background-gray/70 transition-all duration-200 text-base;
}

.core-dataset-upload-btn i {
    @apply text-lg;
}

.core-dataset-file-info {
    @apply mt-3 flex items-center justify-between p-3 bg-echoes-background-gray/30 border border-echoes-border-gray rounded-xl;
}


.dots-loader {
    padding-bottom: 18px;
    width: 18px;
    aspect-ratio: 4;
    background: radial-gradient(circle closest-side,#ffffff 90%,#ffffff00) 0/calc(100%/3.8) 100% space;
    clip-path: inset(0 100% 0 0);
    animation: l1 1s steps(4) infinite;
}
@keyframes l1 {to{clip-path: inset(0 -34% 0 0)}}

.spinner {
    @apply w-10 h-10;
    border: 4px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top: 4px solid #FE3E01;
    animation: spin 1s linear infinite;
    }
</style>